import type { ActionResult, EnemyState, EngineEvent, Position, RunState, SpectateFrame } from '../api/types';
import type { RunAction } from '../api/client';

/**
 * Frontend combat → game-feel event interpretation (spec.md §5).
 *
 * The backend is server-authoritative and returns only a *coarse* `EngineOutcome`
 * (continued / died / descended / item_picked_up / …) plus the new authoritative
 * `RunState`. It does **not** emit granular "enemy took N damage" events. Rather than
 * redesign the backend, this pure layer **diffs the previous and next RunState** to
 * infer the visual events the animation stage, effects, and SFX should fire. It is the
 * documented frontend interpretation seam: if a future backend adds a richer combat
 * event payload, prefer it and keep this as the fallback.
 *
 * Everything here is pure and total: terminal results (died/extracted) carry no next
 * state, so the mapping leans on the action + outcome and never assumes `result.state`.
 */

export type Facing = 'left' | 'right';
export type EntityAnimState = 'idle' | 'walk' | 'attack' | 'hit' | 'death';

export type GameEvent =
  | { kind: 'playerAnim'; state: EntityAnimState; facing?: Facing }
  | { kind: 'enemyAnim'; enemyId: string; state: EntityAnimState }
  | { kind: 'damage'; targetType: 'player' | 'enemy'; enemyId?: string; amount: number; pos: Position }
  | { kind: 'flash'; targetType: 'player' | 'enemy'; enemyId?: string }
  | { kind: 'sfx'; name: string }
  | { kind: 'effect'; name: string; pos: Position }
  | { kind: 'sparkle'; pos: Position }
  | { kind: 'shake'; intensity: number }
  | { kind: 'hitStop'; durationMs: number }
  | { kind: 'pulse'; name: 'extract' | 'descend' };

/** Canonical SFX keys (mirror the audio manifest keys). */
export const SFX = {
  step: 'sfx_step',
  swordSwing: 'sfx_sword_swing',
  hitFlesh: 'sfx_hit_flesh',
  playerHurt: 'sfx_player_hurt',
  monsterDie: 'sfx_monster_die',
  goldPickup: 'sfx_gold_pickup',
  itemPickup: 'sfx_item_pickup',
  descend: 'sfx_descend',
  extract: 'sfx_extract',
  playerDeath: 'sfx_player_death',
} as const;

export const VFX = {
  slash: 'vfx_slash_gravelight_01',
  hitImpact: 'vfx_hit_impact_gravebone_01',
  deathPuff: 'vfx_death_puff_gravebound_01',
  pickupSparkle: 'vfx_pickup_sparkle_01',
  extractionSeal: 'vfx_extraction_seal_01',
} as const;

export type PlayerAttackStyle = 'default' | 'luggage';

export interface DeriveInput {
  action: RunAction;
  /** Authoritative state before the action. */
  prev: RunState;
  /** Full action result (active carries the next state; terminal results do not). */
  result: ActionResult;
  /** Presentation-only style for the player attack. Does not affect combat rules. */
  playerAttackStyle?: PlayerAttackStyle;
}

const facingFromDx = (dx: number): Facing | undefined => (dx > 0 ? 'right' : dx < 0 ? 'left' : undefined);
const samePosition = (a: Position, b: Position): boolean => a.x === b.x && a.y === b.y;

function enemyById(state: RunState | undefined, id: string): EnemyState | undefined {
  return state?.floorState?.enemies?.find((e) => e.id === id);
}

/** Diff enemies between two same-floor states, emitting hit/death + damage/flash/sfx. */
function diffEnemies(prev: RunState, next: RunState, events: GameEvent[]): void {
  const prevEnemies = prev.floorState?.enemies ?? [];
  const nextEnemies = next.floorState?.enemies ?? [];
  const nextById = new Map(nextEnemies.map((e) => [e.id, e]));
  for (const before of prevEnemies) {
    const after = nextById.get(before.id);
    const beforeHp = before.hp;
    const afterHp = after ? after.hp : 0; // removed from list ⇒ dead
    if (afterHp < beforeHp) {
      const pos = after?.position ?? before.position;
      const died = afterHp <= 0;
      events.push({ kind: 'enemyAnim', enemyId: before.id, state: died ? 'death' : 'hit' });
      events.push({ kind: 'damage', targetType: 'enemy', enemyId: before.id, amount: beforeHp - afterHp, pos });
      events.push({ kind: 'flash', targetType: 'enemy', enemyId: before.id });
      events.push({ kind: 'effect', name: VFX.hitImpact, pos });
      if (died) {
        events.push({ kind: 'effect', name: VFX.deathPuff, pos });
        events.push({ kind: 'sfx', name: SFX.monsterDie });
        events.push({ kind: 'shake', intensity: 3 });
      } else {
        events.push({ kind: 'sfx', name: SFX.hitFlesh });
        events.push({ kind: 'shake', intensity: 2 });
      }
    }
  }
}

/** Detect newly-collected gold caches (same floor only). */
function diffGold(prev: RunState, next: RunState, events: GameEvent[]): void {
  const before = new Map((prev.floorState?.goldCaches ?? []).map((g) => [g.id, g]));
  for (const g of next.floorState?.goldCaches ?? []) {
    const was = before.get(g.id);
    if (was && !was.collected && g.collected) {
      events.push({ kind: 'sparkle', pos: g.position });
      events.push({ kind: 'effect', name: VFX.pickupSparkle, pos: g.position });
      events.push({ kind: 'sfx', name: SFX.goldPickup });
    }
  }
}

function pushEnemyDamage(event: Extract<EngineEvent, { type: 'enemy_damaged' }>, events: GameEvent[]): void {
  const died = event.killed || event.hp <= 0;
  const pos = event.position;
  events.push({ kind: 'enemyAnim', enemyId: event.enemyId, state: died ? 'death' : 'hit' });
  events.push({ kind: 'damage', targetType: 'enemy', enemyId: event.enemyId, amount: event.damage, pos });
  events.push({ kind: 'flash', targetType: 'enemy', enemyId: event.enemyId });
  events.push({ kind: 'effect', name: VFX.hitImpact, pos });
  if (died) {
    events.push({ kind: 'effect', name: VFX.deathPuff, pos });
    events.push({ kind: 'sfx', name: SFX.monsterDie });
    events.push({ kind: 'shake', intensity: 3 });
  } else {
    events.push({ kind: 'sfx', name: SFX.hitFlesh });
    events.push({ kind: 'shake', intensity: 2 });
  }
}

function spectateRun(frame: SpectateFrame): RunState {
  return { ...frame.run, accountId: frame.run.playerLabel, seed: '' };
}

function spectateGoldDiff(prev: SpectateFrame | null, current: SpectateFrame, events: GameEvent[]): void {
  if (!prev || prev.run.floor !== current.run.floor) return;
  diffGold(spectateRun(prev), spectateRun(current), events);
}

function fallbackEnemyDamageDiff(prev: SpectateFrame | null, current: SpectateFrame): Array<Extract<EngineEvent, { type: 'enemy_damaged' }>> {
  if (!prev || prev.run.floor !== current.run.floor) return [];
  const beforeEnemies = prev.run.floorState?.enemies ?? [];
  const afterById = new Map((current.run.floorState?.enemies ?? []).map((enemy) => [enemy.id, enemy]));
  const damage: Array<Extract<EngineEvent, { type: 'enemy_damaged' }>> = [];
  for (const before of beforeEnemies) {
    const after = afterById.get(before.id);
    const hp = after?.hp ?? 0;
    if (hp >= before.hp) continue;
    damage.push({
      type: 'enemy_damaged',
      enemyId: before.id,
      damage: before.hp - hp,
      hp,
      killed: hp <= 0,
      position: after?.position ?? before.position,
    });
  }
  return damage;
}

function fallbackPlayerDamage(prev: SpectateFrame | null, current: SpectateFrame): Array<Extract<EngineEvent, { type: 'player_damaged' }>> {
  if (!prev || current.run.hp >= prev.run.hp) return [];
  return [{
    type: 'player_damaged',
    source: 'enemy',
    damage: prev.run.hp - current.run.hp,
    hp: current.run.hp,
    position: current.run.position,
  }];
}

export interface DeriveSpectateInput {
  prev: SpectateFrame | null;
  current: SpectateFrame;
}

/**
 * Convert a public Spectate frame transition into the same client-only GameEvents
 * used by live action responses. This consumes only sanitized replay fields and
 * never calls action endpoints or derives gameplay outcomes.
 */
export function deriveSpectateGameEvents(input: DeriveSpectateInput): GameEvent[] {
  const { prev, current } = input;
  if (!prev && current.action === null && current.events.length === 0) return [];

  const events: GameEvent[] = [];
  const action = current.action ?? '';
  const enemyDamageEvents = current.events.filter((event): event is Extract<EngineEvent, { type: 'enemy_damaged' }> => event.type === 'enemy_damaged');
  const playerDamageEvents = current.events.filter((event): event is Extract<EngineEvent, { type: 'player_damaged' }> => event.type === 'player_damaged');
  const enemyDamage = enemyDamageEvents.length > 0 ? enemyDamageEvents : fallbackEnemyDamageDiff(prev, current);
  const playerDamage = playerDamageEvents.length > 0 ? playerDamageEvents : fallbackPlayerDamage(prev, current);

  for (const event of enemyDamage) pushEnemyDamage(event, events);
  spectateGoldDiff(prev, current, events);

  const currentPos = current.run.position;
  const previousPos = prev?.run.position ?? currentPos;
  const attackLike = action === 'attack' || enemyDamage.length > 0;
  const moveLike = action.startsWith('move:') && !samePosition(previousPos, currentPos);
  const pickupLike = action === 'pickup' || current.outcome === 'item_picked_up';
  const extractLike = action === 'extract' || current.outcome === 'extract_requested';
  const descendLike = action === 'descend' || current.outcome === 'descended';
  const diedLike = current.outcome === 'died' || current.run.status === 'died';
  const moveFacing = facingFromDx(currentPos.x - previousPos.x);

  if (attackLike) {
    const target = enemyDamage[0]?.position;
    events.push({
      kind: 'playerAnim',
      state: 'attack',
      ...(target ? { facing: facingFromDx(target.x - previousPos.x) } : {}),
    });
    events.push({ kind: 'sfx', name: SFX.swordSwing });
    events.push({ kind: 'effect', name: VFX.slash, pos: previousPos });
    events.push({ kind: 'hitStop', durationMs: 50 });
  } else if (moveLike) {
    events.push({ kind: 'playerAnim', state: 'walk', ...(moveFacing ? { facing: moveFacing } : {}) });
    events.push({ kind: 'sfx', name: SFX.step });
  } else if (playerDamage.length > 0) {
    events.push({ kind: 'playerAnim', state: 'hit' });
  }

  for (const event of playerDamage) {
    const pos = event.position ?? current.run.position;
    events.push({ kind: 'damage', targetType: 'player', amount: event.damage, pos });
    events.push({ kind: 'flash', targetType: 'player' });
    events.push({ kind: 'effect', name: VFX.hitImpact, pos });
    events.push({ kind: 'sfx', name: SFX.playerHurt });
    events.push({ kind: 'shake', intensity: 4 });
  }

  if (pickupLike) {
    events.push({ kind: 'sparkle', pos: current.run.position });
    events.push({ kind: 'effect', name: VFX.pickupSparkle, pos: current.run.position });
    events.push({ kind: 'sfx', name: SFX.itemPickup });
  }

  if (extractLike) {
    const pos = prev?.run.floorState.ferryStep ?? current.run.floorState.ferryStep;
    if (pos) {
      events.push({ kind: 'effect', name: VFX.extractionSeal, pos });
      events.push({ kind: 'pulse', name: 'extract' });
    }
    events.push({ kind: 'sfx', name: SFX.extract });
  }

  if (descendLike) {
    events.push({ kind: 'pulse', name: 'descend' });
    events.push({ kind: 'sfx', name: SFX.descend });
  }

  if (diedLike) {
    events.push({ kind: 'playerAnim', state: 'death' });
    events.push({ kind: 'sfx', name: SFX.playerDeath });
    events.push({ kind: 'shake', intensity: 6 });
  }

  return events;
}

export function deriveGameEvents(input: DeriveInput): GameEvent[] {
  const { action, prev, result, playerAttackStyle = 'default' } = input;
  const events: GameEvent[] = [];

  // ── terminal results carry no next state ────────────────────────────────────
  if (result.status === 'died') {
    events.push({ kind: 'playerAnim', state: 'death' });
    events.push({ kind: 'sfx', name: SFX.playerDeath });
    events.push({ kind: 'shake', intensity: 6 });
    return events;
  }
  if (result.status === 'extracted') {
    if (prev.floorState.ferryStep) {
      events.push({ kind: 'effect', name: VFX.extractionSeal, pos: prev.floorState.ferryStep });
      events.push({ kind: 'pulse', name: 'extract' });
    }
    events.push({ kind: 'sfx', name: SFX.extract });
    return events;
  }

  // ── active: we have the next authoritative state ────────────────────────────
  const next = result.state;
  const outcome = result.outcome as { kind?: string } | undefined;
  const sameFloor = next.floor === prev.floor;

  // Combat / pickup diffs only make sense within the same floor.
  if (sameFloor) {
    diffEnemies(prev, next, events);
    diffGold(prev, next, events);
  }

  const playerTookDamage = next.hp < prev.hp;
  const damageAmount = prev.hp - next.hp;

  // Primary player animation state (single state; priority order below).
  let playerState: EntityAnimState = 'idle';
  let facing: Facing | undefined;

  if (action.type === 'attack') {
    playerState = 'attack';
    const target = enemyById(prev, action.targetId);
    if (target) facing = facingFromDx(target.position.x - prev.position.x);
    if (playerAttackStyle === 'default') {
      events.push({ kind: 'sfx', name: SFX.swordSwing });
      events.push({ kind: 'effect', name: VFX.slash, pos: prev.position });
    }
    events.push({ kind: 'hitStop', durationMs: 50 });
  } else if (action.type === 'move' && (next.position.x !== prev.position.x || next.position.y !== prev.position.y)) {
    playerState = 'walk';
    facing = facingFromDx(next.position.x - prev.position.x);
    events.push({ kind: 'sfx', name: SFX.step });
  } else if (playerTookDamage) {
    playerState = 'hit';
  }

  events.push({ kind: 'playerAnim', state: playerState, ...(facing ? { facing } : {}) });

  // Player-damage feedback fires regardless of the chosen primary state.
  if (playerTookDamage) {
    events.push({ kind: 'damage', targetType: 'player', amount: damageAmount, pos: next.position });
    events.push({ kind: 'flash', targetType: 'player' });
    events.push({ kind: 'effect', name: VFX.hitImpact, pos: next.position });
    events.push({ kind: 'sfx', name: SFX.playerHurt });
    events.push({ kind: 'shake', intensity: 4 });
  }

  // Outcome-driven events.
  if (outcome?.kind === 'item_picked_up') {
    events.push({ kind: 'sparkle', pos: next.position });
    events.push({ kind: 'effect', name: VFX.pickupSparkle, pos: next.position });
    events.push({ kind: 'sfx', name: SFX.itemPickup });
  }
  if (outcome?.kind === 'descended' || action.type === 'descend') {
    events.push({ kind: 'pulse', name: 'descend' });
    events.push({ kind: 'sfx', name: SFX.descend });
  }

  return events;
}
